import { ActionIcon, Group, Text, ThemeIcon, Box } from '@mantine/core';
import { IconChevronLeft } from '@tabler/icons-react';
import { ReactNode } from 'react';

interface DrawerHeaderProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  color?: string;
  rightSection?: ReactNode;
  /**
   * Renders a back chevron for a drawer reached from another drawer, so the
   * user can step back instead of starting over. The chevron takes the
   * ThemeIcon's slot (navigation replaces decoration) to keep the header
   * calm.
   */
  onBack?: () => void;
  /** Accessible label for the back chevron (name the place it returns to). */
  backLabel?: string;
  /** Small qualifier chip rendered inline after the title (e.g. Experimental). */
  badge?: ReactNode;
}

export const DrawerHeader = ({ icon, title, subtitle, color = 'orange', rightSection, onBack, backLabel = 'Back', badge }: DrawerHeaderProps) => (
  <Group gap="sm" align="center" wrap="nowrap" justify="space-between" style={{ width: '100%' }}>
    <Group gap="sm" align="center" wrap="nowrap">
      {/* On the mobile bottom sheet this header doubles as the swipe-to-close
          drag handle; stop pointerdown so pressing back never starts the drag. */}
      {onBack ? (
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label={backLabel}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onBack}
        >
          <IconChevronLeft size={20} />
        </ActionIcon>
      ) : (
        <ThemeIcon variant="light" color={color} size="lg" radius="md">
          {icon}
        </ThemeIcon>
      )}
      <Box>
        <Group gap={6} wrap="nowrap">
          <Text fw={600} size="md" lh={1.3}>
            {title}
          </Text>
          {badge}
        </Group>
        {subtitle && (
          <Text size="xs" c="dimmed" lh={1.3}>
            {subtitle}
          </Text>
        )}
      </Box>
    </Group>
    {/* On the mobile bottom sheet this header doubles as the swipe-to-close
        drag handle; stop pointerdown here so pressing header controls never
        starts the sheet drag. */}
    {rightSection && (
      <Box onPointerDown={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
        {rightSection}
      </Box>
    )}
  </Group>
);
